import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { ActivityCalendarView } from '../components/activity-calendar';
import type { Manifest } from '../content/data';
import { routeHref } from '../navigation/routes';
import {
  activityCountsFromProgress,
  buildActivityCalendar,
  calculateStreaks,
  formatActivityDate,
  localDateKey
} from '../progress/activity';
import {
  allDayIds,
  completedDayCount,
  firstIncompleteDayId,
  type LearnerProfile
} from '../progress/progress';
import { useProgress, useProgressActions } from '../progress/progress-context';
import { createSummarySvg } from '../progress/summary-export';

function FlameIcon() {
  return (
    <svg className="streak-flame" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.7 2.5c.4 3.1-1.7 4.3-3.2 6.1-1.1 1.3-1.7 2.8-1.1 4.6.5-1.2 1.4-2.1 2.5-2.8-.2 2.2 1.8 3 1.8 5.1 0 1.7-1.2 3-2.9 3-2.8 0-4.6-2.2-4.6-5.2 0-3.8 2.9-7.4 7.5-10.8Z" />
      <path d="M14.7 8.3c2.1 1.8 3.2 4 3.2 6.4 0 3.8-2.5 6.8-6.2 6.8-1.4 0-2.7-.4-3.7-1.2 4.8.6 8.5-3.5 6.7-12Z" />
    </svg>
  );
}

function learnerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'FF';
  return parts.slice(0, 2).map((part) => Array.from(part)[0]?.toUpperCase() ?? '').join('');
}

function dayPosition(dayId: string | null): string {
  const match = dayId?.match(/^week-(\d{2})-day-(\d{2})$/);
  if (!match) return 'Curriculum complete';
  return `Week ${Number(match[1])}, Day ${Number(match[2])}`;
}

function ProfilePanel({
  profile,
  place,
  completed,
  total,
  onSave
}: {
  profile: LearnerProfile;
  place: string;
  completed: number;
  total: number;
  onSave: (profile: LearnerProfile) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(profile);
  const displayName = profile.name.trim() || 'Frontend learner';
  const displayRole = profile.targetRole.trim() || 'Frontend developer';
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const position = place === 'Curriculum complete' ? place : `Up next: ${place}`;

  const openEditor = () => {
    setDraft(profile);
    setEditing(true);
  };

  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({
      name: draft.name.trim(),
      targetRole: draft.targetRole.trim() || 'Frontend developer'
    });
    setEditing(false);
  };

  return (
    <>
      <section className="summary-profile" aria-labelledby="learner-profile-heading">
        <span className="profile-initials" aria-hidden="true">{learnerInitials(displayName)}</span>
        <div className="profile-copy">
          <h2 id="learner-profile-heading">{displayName}</h2>
          <p>{displayRole}</p>
          <span>{position}</span>
        </div>
        <div className="profile-progress">
          <div>
            <span>Curriculum progress</span>
            <strong>{completed} / {total}</strong>
          </div>
          <div className="summary-progress-track" role="progressbar" aria-label={`${percent}% of curriculum days complete`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
            <span style={{ width: `${percent}%` }} />
          </div>
        </div>
        <button className="secondary-button" type="button" onClick={openEditor}>Edit profile</button>
      </section>

      {editing ? (
        <form className="profile-editor" aria-label="Edit learner profile" onSubmit={save}>
          <label>
            <span>Name</span>
            <input
              autoFocus
              maxLength={80}
              value={draft.name}
              placeholder="Your name"
              onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
            />
          </label>
          <label>
            <span>Target role</span>
            <input
              maxLength={100}
              value={draft.targetRole}
              placeholder="Frontend developer"
              onChange={(event) => setDraft((current) => ({ ...current, targetRole: event.target.value }))}
            />
          </label>
          <div className="profile-editor-actions">
            <button className="primary-button" type="submit">Save profile</button>
            <button className="text-button" type="button" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      ) : null}
    </>
  );
}

function Metric({ label, value, detail, children }: {
  label: string;
  value: string;
  detail: string;
  children?: ReactNode;
}) {
  return (
    <div className="summary-metric">
      <dt>{label}</dt>
      <dd>
        <strong>{children}{value}</strong>
        <span>{detail}</span>
      </dd>
    </div>
  );
}

export function SummaryPage({ manifest }: { manifest: Manifest }) {
  const progress = useProgress();
  const { setProfile } = useProgressActions();
  const [exportNotice, setExportNotice] = useState(false);
  const validDayIds = useMemo(() => new Set(allDayIds(manifest)), [manifest]);
  const completedValidDayIds = useMemo(
    () => new Set(progress.completedDayIds.filter((dayId) => validDayIds.has(dayId))),
    [progress.completedDayIds, validDayIds]
  );
  const completed = completedDayCount(manifest, progress);
  const currentDayId = firstIncompleteDayId(manifest, progress);
  const place = dayPosition(currentDayId);
  const questionAttempts = Object.values(progress.questionAttempts).filter((attempt) => attempt.attempted).length;
  const displayName = progress.profile.name.trim() || 'Frontend learner';
  const displayRole = progress.profile.targetRole.trim() || 'Frontend developer';
  const currentWeek = currentDayId
    ? manifest.weeks.find((week) => currentDayId.startsWith(week.id))
    : manifest.weeks[manifest.weeks.length - 1];
  const currentWeekDayIds = currentWeek
    ? Array.from({ length: 7 }, (_, index) => `${currentWeek.id}-day-${String(index + 1).padStart(2, '0')}`)
    : [];
  const currentWeekCompleted = currentWeekDayIds.filter((dayId) => completedValidDayIds.has(dayId)).length;
  const currentWeekPercent = Math.round((currentWeekCompleted / 7) * 100);
  const completedMilestones = manifest.weeks.filter((week) =>
    Array.from({ length: 7 }, (_, index) => `${week.id}-day-${String(index + 1).padStart(2, '0')}`)
      .every((dayId) => completedValidDayIds.has(dayId))
  ).length;

  const activity = useMemo(() => {
    const counts = activityCountsFromProgress(
      { completedAtByDay: progress.completedAtByDay },
      completedValidDayIds
    );
    const today = new Date();
    return {
      calendar: buildActivityCalendar(counts, today),
      streaks: calculateStreaks(counts, today)
    };
  }, [progress.completedAtByDay, completedValidDayIds]);

  const recentCompletions = useMemo(() =>
    Object.entries(progress.completedAtByDay)
      .filter(([dayId]) => completedValidDayIds.has(dayId))
      .sort((left, right) => right[1].localeCompare(left[1]))
      .slice(0, 5)
      .map(([dayId, timestamp]) => ({
        dayId,
        place: dayPosition(dayId),
        dateKey: localDateKey(new Date(timestamp))
      })),
    [progress.completedAtByDay, completedValidDayIds]
  );

  const datedDayIds = new Set(
    Object.keys(progress.completedAtByDay).filter((dayId) => completedValidDayIds.has(dayId))
  );
  const undatedCompletions = [...new Set(progress.completedDayIds)]
    .filter((dayId) => validDayIds.has(dayId) && !datedDayIds.has(dayId)).length;

  const nextSessionHeading = currentDayId
    ? `${completed === 0 ? 'Start' : 'Continue'} with ${place}`
    : 'Curriculum complete';
  const nextSessionText = currentDayId
    ? 'This is the first unfinished day. Your place stays here until you mark it complete.'
    : 'Every curriculum day is complete. Use the roadmap to choose what to review or rebuild.';

  const exportSummary = () => {
    const now = new Date();
    const svg = createSummarySvg({
      name: displayName,
      targetRole: displayRole,
      completedDays: completed,
      totalDays: manifest.totals.days,
      currentStreak: activity.streaks.current,
      longestStreak: activity.streaks.longest,
      activeDays: activity.streaks.activeDays,
      questionAttempts,
      calendar: activity.calendar,
      generatedOn: formatActivityDate(localDateKey(now))
    });
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `frontend-fieldbook-summary-${localDateKey(now)}.svg`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    setExportNotice(true);
  };

  return (
    <main className="wide-page summary-page" id="main-content">
      <header className="summary-heading">
        <h1>Your progress</h1>
        <p>See where you stopped and continue from there. The calendar records each date when you mark a curriculum day complete.</p>
      </header>

      <ProfilePanel
        profile={progress.profile}
        place={place}
        completed={completed}
        total={manifest.totals.days}
        onSave={setProfile}
      />

      <section className="next-session" aria-labelledby="next-session-heading">
        <div>
          <h2 id="next-session-heading">{nextSessionHeading}</h2>
          <p>{nextSessionText}</p>
        </div>
        <a className="primary-button" href={currentDayId ? '#/today' : '#/roadmap'}>
          {currentDayId ? `Continue ${place}` : 'Review the roadmap'}
        </a>
      </section>

      <dl className="summary-metrics">
        <Metric
          label="Current streak"
          value={`${activity.streaks.current} ${activity.streaks.current === 1 ? 'day' : 'days'}`}
          detail={activity.streaks.current > 0 ? 'Consecutive completion dates' : 'Complete a day to start'}
        >
          <FlameIcon />
        </Metric>
        <Metric
          label="Best streak"
          value={`${activity.streaks.longest} ${activity.streaks.longest === 1 ? 'day' : 'days'}`}
          detail="Longest run so far"
        />
        <Metric
          label="Project milestones"
          value={`${completedMilestones} / ${manifest.totals.weeks}`}
          detail="Weekly checkpoints completed"
        />
        <Metric
          label="Interview answers"
          value={String(questionAttempts)}
          detail="Questions answered aloud"
        />
      </dl>

      {currentWeek ? (
        <section className="current-week-summary" aria-labelledby="current-week-heading">
          <div className="current-week-heading">
            <div>
              <h2 id="current-week-heading">Your current week</h2>
              <p>Week {currentWeek.number}: {currentWeek.title}</p>
            </div>
            <strong>{currentWeekCompleted} / 7 days complete</strong>
          </div>

          <div className="current-week-layout">
            <div className="week-day-progress">
              <div
                className="week-progress-track"
                role="progressbar"
                aria-label={`${currentWeekPercent}% of Week ${currentWeek.number} complete`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={currentWeekPercent}
              >
                <span style={{ width: `${currentWeekPercent}%` }} />
              </div>
              <ol aria-label={`Week ${currentWeek.number} progress`}>
                {currentWeekDayIds.map((dayId, index) => {
                  const state = completedValidDayIds.has(dayId)
                    ? 'done'
                    : dayId === currentDayId
                      ? 'next'
                      : 'later';
                  return (
                    <li className={`week-day-status week-day-status--${state}`} key={dayId}>
                      <span aria-hidden="true">{index + 1}</span>
                      <small aria-hidden="true">{state === 'done' ? 'Done' : state === 'next' ? 'Next' : 'Later'}</small>
                      <span className="sr-only">Day {index + 1}: {state === 'done' ? 'complete' : state === 'next' ? 'up next' : 'not started'}</span>
                    </li>
                  );
                })}
              </ol>
              <a className="text-button" href={routeHref({ page: 'week', weekId: currentWeek.id })}>
                View Week {currentWeek.number} plan
              </a>
            </div>

            <aside className="week-milestone" aria-labelledby="week-milestone-heading">
              <span>Project milestone</span>
              <h3 id="week-milestone-heading">{currentWeek.mainProject}</h3>
              <p>{currentWeek.milestone}</p>
              <a className="text-button" href={routeHref({ page: 'project', weekId: currentWeek.id })}>
                View requirements
              </a>
            </aside>
          </div>
        </section>
      ) : null}

      <section className="activity-section" aria-labelledby="activity-heading">
        <div className="activity-section-heading">
          <div>
            <h2 id="activity-heading">Your activity</h2>
            <p>Each square is one date. Green means you completed at least one curriculum day on that date.</p>
          </div>
          <strong>{activity.calendar.rangeLabel}</strong>
        </div>
        {activity.calendar.activeDaysInRange === 0 ? (
          <div className="activity-empty-state">
            <strong>No completion dates yet</strong>
            <span>Mark the current curriculum day complete to add your first green square.</span>
          </div>
        ) : null}
        <p className="activity-scroll-hint">Scroll sideways to see earlier months.</p>
        <ActivityCalendarView calendar={activity.calendar} />
        <details className="activity-help">
          <summary>How activity and streaks work</summary>
          <div>
            <p>Only <strong>Mark day complete</strong> adds activity. Opening or reading a page does not. More than one completion on the same date makes the square darker.</p>
            <p>A streak counts consecutive completion dates. If your last completion was yesterday, the current streak stays available today.</p>
          </div>
        </details>
        {undatedCompletions > 0 ? (
          <p className="activity-history-note">{undatedCompletions} earlier {undatedCompletions === 1 ? 'completion is' : 'completions are'} included in course progress without a calendar date. The app will date new completions from now on.</p>
        ) : null}
      </section>

      <div className="summary-lower-grid">
        <section className="recent-completions" aria-labelledby="recent-completions-heading">
          <h2 id="recent-completions-heading">Recent completions</h2>
          {recentCompletions.length > 0 ? (
            <ol>
              {recentCompletions.map((item) => (
                <li key={item.dayId}>
                  <span>{item.place}</span>
                  <time dateTime={item.dateKey}>{formatActivityDate(item.dateKey)}</time>
                </li>
              ))}
            </ol>
          ) : (
            <p>Completed days will appear here with the date they were finished.</p>
          )}
        </section>

        <section className="summary-export" aria-labelledby="summary-export-heading">
          <h2 id="summary-export-heading">Save or share</h2>
          <p>Download a summary image for sharing. Use a progress backup when you need to move or restore your saved work.</p>
          <div className="summary-export-actions">
            <button className="secondary-button" type="button" onClick={exportSummary}>Download summary image</button>
            <a className="text-button" href="#/settings">Back up progress</a>
          </div>
          {exportNotice ? (
            <p className="notice notice--success" role="status">Summary image downloaded. It cannot restore progress.</p>
          ) : null}
        </section>
      </div>
    </main>
  );
}
